/**
 * /accounts/:aid/byo — "bring your own" Voice PE setup for markets we can't ship
 * a flashed device to. In desktop Chrome/Edge the caregiver flashes our firmware
 * over USB (Web Serial), pushes Wi-Fi over the same USB link (Improv-over-Serial
 * — no captive portal, no network-hop), and we auto-claim the device. Steps:
 *   flash    — fetch the factory bin (firmware_manifest) + flash over USB + reboot
 *   wifi     — one form → Improv pushes creds over USB → device joins + registers
 *   claiming — read the setup code off serial, poll device_setup_status, claim
 *   done     — claim starts the free trial (like any device); add a card later
 *              from Settings → Manage billing to continue
 *
 * The flash + Improv + reboot can only be exercised against real hardware on
 * desktop Chrome/Edge (see byoFlasher.ts / improvSerial.ts) — NOT headless.
 * Identity is device-generated under the merged model; the browser never touches
 * the MAC. BYO now gets the same card-optional free trial at claim as everyone
 * else (no separate immediate-billing checkout at setup).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { Button } from '@/components/ui/Button';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import {
  firmwareManifest,
  deviceSetupStatus,
  setupDevice,
} from '@/lib/callables';
import { connectFlasher, webSerialSupported, type ByoFlasher } from '@/lib/byoFlasher';
import { ImprovSerial, ImprovError, type ScannedNetwork } from '@/lib/improvSerial';
import { canonicalizeSetupCode } from '@/lib/setupCode';
import { log } from '@/lib/logger';

type Step = 'flash' | 'reboot' | 'wifi' | 'claiming' | 'done';

function humanError(e: unknown): string {
  if (e instanceof Error) return e.message.replace(/^FirebaseError:\s*/, '');
  return 'Something went wrong.';
}

/** Resolve true once `pred()` holds, or false after `timeoutMs`. */
function waitFor(pred: () => boolean, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const id = setInterval(() => {
      if (pred()) {
        clearInterval(id);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(id);
        resolve(false);
      }
    }, 250);
  });
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-lg px-4 py-8">{children}</div>;
}

export function ByoSetupPage() {
  const { aid } = useParams<{ aid: string }>();

  // BYO DISABLED 2026-07-16 — UK-only while insurance and compliance cover
  // UK customers; a browser-flash route lets anyone anywhere self-provision
  // service, which defeats that. Entry points were removed from Order.tsx +
  // AddDeviceDialog.tsx; this notice covers direct URLs/bookmarks. The full
  // wizard lives on as ByoSetupWizard below — re-enable by returning
  // <ByoSetupWizard /> here instead.
  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>Setting up your own device isn't available right now</CardTitle>
          <CardDescription>
            RemindMeVoice is available in the UK only for now, and buttons are
            supplied by us ready to use.
          </CardDescription>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-ink-soft">
            If you're outside the UK and would like RemindMeVoice, email{' '}
            <a
              href="mailto:hello@remindmevoice.com?subject=RemindMeVoice%20outside%20the%20UK"
              className="font-medium text-accent-700 underline"
            >
              hello@remindmevoice.com
            </a>{' '}
            and we'll let you know as soon as it's available in your country.
            In the UK,{' '}
            <Link to={`/accounts/${aid}/order`} className="font-medium text-accent-700 underline">
              order a button
            </Link>{' '}
            and it arrives ready to plug in.
          </p>
        </CardBody>
      </Card>
    </Shell>
  );
}

/** The full BYO wizard, retained for re-enable (swap ByoSetupPage's body for
 * `return <ByoSetupWizard />;`). A real component — not unreachable code —
 * so type narrowing, hooks rules and noUnusedLocals all stay satisfied;
 * exported although nothing routes to it. */
export function ByoSetupWizard() {
  const { aid } = useParams<{ aid: string }>();
  const auth = useAuth();
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const { memberships } = useMyMemberships(uid);

  const [accountId, setAccountId] = useState(aid ?? '');
  useEffect(() => {
    if (aid) setAccountId(aid);
  }, [aid]);

  const [step, setStep] = useState<Step>('flash');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flashPct, setFlashPct] = useState<number | null>(null);
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [room, setRoom] = useState('');
  const [code, setCode] = useState('');
  // True once Improv has pushed Wi-Fi and the device is joining — so a claim
  // retry doesn't re-send Wi-Fi to an already-provisioned device (which would
  // just hang waiting for a fresh PROVISIONED state).
  const [provisioned, setProvisioned] = useState(false);
  const [networks, setNetworks] = useState<ScannedNetwork[]>([]);
  const [scanning, setScanning] = useState(false);
  const [manualSsid, setManualSsid] = useState(false);

  // Serial session carried across steps. The improv client + port live until
  // the claim succeeds (or the user leaves); cleaned up on unmount.
  const flasherRef = useRef<ByoFlasher | null>(null);
  const portRef = useRef<SerialPort | null>(null);
  const improvRef = useRef<ImprovSerial | null>(null);
  const codeRef = useRef('');

  const supported = useMemo(() => webSerialSupported(), []);
  const showAccountPicker = memberships.length > 1;

  async function teardownSerial() {
    try {
      await improvRef.current?.stop();
    } catch {
      /* ignore */
    }
    // Release esptool's transport too — on a flash failure (before reboot) it
    // still holds the original port; without this the port stays locked and a
    // retry's connectFlasher can't open it.
    try {
      await flasherRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    try {
      await portRef.current?.close();
    } catch {
      /* ignore */
    }
    improvRef.current = null;
    portRef.current = null;
    flasherRef.current = null;
  }

  useEffect(() => {
    // Cleanup any open serial session when leaving the page.
    return () => {
      void teardownSerial();
    };
  }, []);

  function captureCode(line: string): void {
    const m = line.match(/RMV-SETUP-CODE:\s*([0-9A-Za-z]+)/);
    if (m?.[1]) {
      const c = canonicalizeSetupCode(m[1]);
      codeRef.current = c;
      setCode(c);
    }
  }

  /** Move to the Wi-Fi step and kick off a best-effort network scan (the device
   * only sees networks it can reach — e.g. 2.4 GHz). Falls back to manual entry. */
  function startWifiStep(improv: ImprovSerial): void {
    setStep('wifi');
    setScanning(true);
    improv
      .scanNetworks()
      .then(setNetworks)
      .catch((e) => log.warn('byo: wifi scan failed', { error: String(e) }))
      .finally(() => setScanning(false));
  }

  async function onFlash() {
    if (!accountId) {
      setError('Pick which person this button is for first.');
      return;
    }
    setBusy(true);
    setError(null);
    setFlashPct(null);
    codeRef.current = ''; // boot-check below waits for a FRESH code marker
    setCode('');
    let flasher: ByoFlasher | null = null;
    try {
      // Connect FIRST so a cancelled picker fails fast before the multi-MB fetch.
      flasher = await connectFlasher();
      flasherRef.current = flasher;
      const manifest = await firmwareManifest({});
      const res = await fetch(manifest.data.factoryUrl);
      if (!res.ok) {
        // A non-OK here is most often the firmware bucket's CORS config —
        // see docs/firmware-notes.md. Keep the user-facing copy plain.
        log.error('byo: firmware download failed', { status: res.status });
        throw new Error(
          `couldn't download RemindMeVoice (error ${res.status}). Please check ` +
            'your internet connection and try again.',
        );
      }
      const bin = new Uint8Array(await res.arrayBuffer());
      const expected = manifest.data.factorySizeBytes;
      if (expected && bin.length !== expected) {
        throw new Error('the download didn’t finish properly — please try again.');
      }
      await flasher.flash(bin, ({ written, total }) =>
        setFlashPct(total ? Math.round((written / total) * 100) : null),
      );
      // Try to reboot into the app over USB. Then VERIFY it actually booted —
      // the device emits RMV-SETUP-CODE every 5s while unregistered, so a fresh
      // code marker means the app is running (not stuck in download mode). If
      // the reset didn't take, fall back to a manual power-cycle.
      try {
        const port = await flasher.rebootIntoFirmware();
        portRef.current = port;
        const improv = new ImprovSerial(port, captureCode);
        improv.start();
        improvRef.current = improv;
        // Verify the app actually booted (it emits RMV-SETUP-CODE every 5s while
        // unregistered); if the reset didn't take, fall back to a manual power-cycle.
        const booted = await waitFor(() => !!codeRef.current, 12_000);
        if (booted) {
          startWifiStep(improv);
        } else {
          // Reset didn't boot the app — tear down this port and ask for a manual
          // power-cycle (the reliable path).
          await improv.stop();
          try {
            await port.close();
          } catch {
            /* ignore */
          }
          improvRef.current = null;
          portRef.current = null;
          setStep('reboot');
        }
      } catch (re) {
        log.warn('byo: programmatic reboot failed; manual power-cycle', { error: String(re) });
        setStep('reboot');
      }
    } catch (e) {
      log.error('byo: flash failed', { error: String(e) });
      setError(humanError(e));
      await teardownSerial();
    } finally {
      setBusy(false);
    }
  }

  async function onReconnect() {
    if (!flasherRef.current) {
      setError('Lost the connection — please start again.');
      setStep('flash');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const port = await flasherRef.current.reconnect();
      portRef.current = port;
      const improv = new ImprovSerial(port, captureCode);
      improv.start();
      improvRef.current = improv;
      // Manual power-cycle means the app is definitely running — go straight on.
      startWifiStep(improv);
    } catch (e) {
      setError(`${humanError(e)} Make sure the button is plugged back in, then try again.`);
    } finally {
      setBusy(false);
    }
  }

  async function onSendWifi() {
    if (!improvRef.current) {
      setError('Lost the connection to your button — please start again.');
      setStep('flash');
      return;
    }
    if (!provisioned && !ssid.trim()) {
      setError('Enter your Wi-Fi network name.');
      return;
    }
    setBusy(true);
    setError(null);
    if (!provisioned) {
      try {
        await improvRef.current.sendWifi(ssid.trim(), password);
        setProvisioned(true);
      } catch (e) {
        // Wrong password / can't join → let them re-enter WITHOUT re-flashing.
        if (e instanceof ImprovError && e.code === 0x03) {
          setError("Couldn't join that Wi-Fi — check the network name and password, then try again.");
        } else {
          setError(humanError(e));
        }
        setBusy(false);
        return;
      }
    }
    // Device joined Wi-Fi + is registering (TOFU). Move to claim.
    setStep('claiming');
    try {
      await claim();
      // Show success first, THEN release the serial session (teardown can't
      // throw, but the claim is already durable so order doesn't risk the bind).
      setStep('done');
      await teardownSerial(); // done with USB; Stripe redirect would drop it anyway
    } catch (e) {
      log.error('byo: claim failed', { error: String(e) });
      setError(humanError(e));
      setStep('wifi'); // provisioned=true now, so a retry skips the Wi-Fi push
    } finally {
      setBusy(false);
    }
  }

  /** Wait for the code (off serial) + for the device to register AND be connected
   *  to us, then claim. Improv confirms the Wi-Fi join but not that the device
   *  reached our backend — and a re-claim (unlinked) device stays `registered`
   *  even while offline — so we also require the explicit `connected` flag
   *  (matches SetupDevice.tsx). */
  async function claim(): Promise<void> {
    const deadline = Date.now() + 120_000;
    for (;;) {
      const c = codeRef.current;
      if (c) {
        const status = await deviceSetupStatus({ setupCode: c });
        if (status.data.registered && status.data.connected) {
          // Claim starts the free trial (card-optional) just like any other
          // device — BYO users get the same trial, then add a card in Settings.
          await setupDevice({ setupCode: c, accountId, roomLabel: room });
          return;
        }
      }
      if (Date.now() > deadline) {
        throw new Error(
          c
            ? "Your button joined Wi-Fi but we can't reach it through our servers yet. " +
              'Check the network has internet access, then try again.'
            : "Couldn't read the setup code from your button — unplug/replug it and start again.",
        );
      }
      await new Promise((r) => setTimeout(r, 3_000));
    }
  }

  if (!supported) {
    return (
      <Shell>
        <Card>
          <CardHeader>
            <CardTitle>Use Chrome or Edge on a computer</CardTitle>
            <CardDescription>
              Setting up your own Voice PE connects to it over USB, which only
              works in Chrome or Edge on a desktop or laptop — not on a phone, and
              not in Safari or Firefox.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-ink-soft">
              Open this page in Chrome or Edge on a computer, plug your Voice PE in
              with a USB cable, and come back here.
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>Set up your own Voice PE</CardTitle>
          <CardDescription>
            Install RemindMeVoice on a Home Assistant Voice PE you already own,
            connect it to your Wi-Fi, and activate it — all through a USB cable.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-5">
          {showAccountPicker && step === 'flash' && (
            <Field label="Set up for" htmlFor="aid">
              <select
                id="aid"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                {memberships.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.accountName || 'Unnamed person'}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {step === 'flash' && (
            <>
              <p className="text-sm text-ink-soft">
                Don&apos;t have one yet?{' '}
                <ExternalLink href="https://www.home-assistant.io/voice-pe/">
                  Buy a Home Assistant Voice PE
                </ExternalLink>
                , then come back here.
              </p>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-soft">
                <li>
                  Plug your Voice PE into this computer with a{' '}
                  <span className="font-medium">data-capable USB-C cable</span> —
                  some cables only charge and won&apos;t work.
                </li>
                <li>
                  Click below, then choose your Voice PE from the list that
                  appears — it may be named{' '}
                  <span className="font-medium">“USB JTAG/serial debug unit”</span>.
                </li>
                <li>We&apos;ll install RemindMeVoice (about 1–2 minutes) — don&apos;t unplug it.</li>
              </ol>
              {flashPct !== null && (
                <p className="text-sm text-ink">Installing… {flashPct}%</p>
              )}
              <Button onClick={onFlash} disabled={busy}>
                {busy ? 'Working…' : 'Connect & install'}
              </Button>
            </>
          )}

          {step === 'reboot' && (
            <>
              <p className="text-sm font-medium text-ink">RemindMeVoice installed ✓</p>
              <p className="text-sm text-ink-soft">
                Now <span className="font-medium">unplug the Voice PE and plug it
                back in</span> to start it up. Its light comes on after a few
                seconds. Then continue.
              </p>
              <Button onClick={onReconnect} disabled={busy}>
                {busy ? 'Reconnecting…' : 'Continue'}
              </Button>
            </>
          )}

          {step === 'wifi' && !provisioned && (
            <>
              <p className="text-sm text-ink-soft">
                Now tell the button which Wi-Fi to join. We&apos;ll send it over
                the USB cable — no need to switch networks.
              </p>
              <p className="text-xs text-ink-muted">
                Use a <span className="font-medium">2.4 GHz</span> network — the
                button can&apos;t connect to 5 GHz-only Wi-Fi.
              </p>
              {networks.length > 0 && !manualSsid ? (
                <Field label="Wi-Fi network" htmlFor="ssid-select">
                  <select
                    id="ssid-select"
                    className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                    value={ssid}
                    onChange={(e) => {
                      if (e.target.value === '__other__') {
                        setManualSsid(true);
                        setSsid('');
                      } else {
                        setSsid(e.target.value);
                      }
                    }}
                  >
                    <option value="" disabled>
                      Choose your network…
                    </option>
                    {networks.map((n) => (
                      <option key={n.ssid} value={n.ssid}>
                        {n.ssid}
                      </option>
                    ))}
                    <option value="__other__">Other / hidden network…</option>
                  </select>
                </Field>
              ) : (
                <Field label="Wi-Fi network name" htmlFor="ssid">
                  <Input id="ssid" value={ssid} onChange={(e) => setSsid(e.target.value)} autoFocus />
                  {scanning && <p className="mt-1 text-xs text-ink-muted">Looking for nearby networks…</p>}
                  {networks.length > 0 && (
                    <button
                      type="button"
                      className="mt-1 text-xs text-accent-700 underline"
                      onClick={() => setManualSsid(false)}
                    >
                      Choose from the list instead
                    </button>
                  )}
                </Field>
              )}
              <Field label="Wi-Fi password" htmlFor="psk">
                <Input
                  id="psk"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field label="Room (optional)" htmlFor="room">
                <Input
                  id="room"
                  placeholder="Bedside"
                  value={room}
                  onChange={(e) => setRoom(e.target.value)}
                  maxLength={100}
                />
              </Field>
              <Button onClick={onSendWifi} disabled={busy}>
                {busy ? 'Connecting…' : 'Connect to Wi-Fi'}
              </Button>
            </>
          )}

          {step === 'wifi' && provisioned && (
            <>
              <p className="text-sm text-ink-soft">
                Your button is connected to Wi-Fi, but we couldn&apos;t finish
                setting it up. Make sure your network has internet access, then
                try again.
              </p>
              <Button onClick={onSendWifi} disabled={busy}>
                {busy ? 'Finishing…' : 'Try again'}
              </Button>
            </>
          )}

          {step === 'claiming' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <Spinner />
              <p className="text-sm text-ink-soft">
                Connecting your button and finishing setup…
              </p>
              {code && (
                <p className="text-xs text-ink-muted">
                  Found your button (code <span className="font-mono">{code}</span>).
                </p>
              )}
            </div>
          )}

          {step === 'done' && (
            <>
              <p className="text-sm font-medium text-ink">
                Your button is set up, and your free trial has started.
              </p>
              <p className="text-sm text-ink-soft">
                It&apos;s free for now — add a payment method any time from
                Settings → Manage billing to keep the service going afterwards
                (£14.99/month).
              </p>
            </>
          )}

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </CardBody>
      </Card>
    </Shell>
  );
}
